import React, { useEffect, useState } from "react";
import { getCdpLandingPages, getCdpContentInfluence, getCdpPeakHours } from "../../api";
import PanelTakeaway from "../shared/PanelTakeaway";

/* ───────────── helpers ───────────── */
function stripDomain(url) {
  if (!url) return "";
  try { return new URL(url).pathname; } catch { return url; }
}
function truncate(str, max = 40) {
  if (!str || str.length <= max) return str || "";
  return str.slice(0, max) + "\u2026";
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOUR_LABELS = ["12a", "1a", "2a", "3a", "4a", "5a", "6a", "7a", "8a", "9a", "10a", "11a", "12p", "1p", "2p", "3p", "4p", "5p", "6p", "7p", "8p", "9p", "10p", "11p"];

/* ═══════════════════ COMPONENT ═══════════════════ */
export default function CdpContentTab({ clientId, rangeStart, rangeEnd, includeBots = false, say }) {
  const [landing, setLanding] = useState([]);
  const [landingLoading, setLandingLoading] = useState(false);
  const [influence, setInfluence] = useState([]);
  const [influenceLoading, setInfluenceLoading] = useState(false);
  const [influenceBaseline, setInfluenceBaseline] = useState(null); // site baseline conversion rate (influence_score 100 = this)
  const [peak, setPeak] = useState([]);
  const [peakLoading, setPeakLoading] = useState(false);
  const [peakMode, setPeakMode] = useState("all"); // "all" | "high_intent"

  useEffect(() => {
    if (!clientId || !rangeStart || !rangeEnd) return;

    const load = async (fn, setter, loadSetter) => {
      try {
        loadSetter(true);
        const resp = await fn(clientId, rangeStart, rangeEnd, { includeBots });
        setter(resp?.data || []);
      } catch (e) {
        console.error(e);
        setter([]);
      } finally {
        loadSetter(false);
      }
    };

    load(getCdpLandingPages, setLanding, setLandingLoading);
    // fn_get_cdp_content_influence returns { baseline_conversion_rate, pages: [...] }
    // (an object, not an array). Each page has: landing_page, visitors, engaged,
    // converted, conversion_rate, engagement_rate, influence_score (100 = baseline),
    // avg_time_seconds. Capture the baseline for the score caption.
    load(getCdpContentInfluence, (data) => {
      setInfluence(Array.isArray(data?.pages) ? data.pages : []);
      setInfluenceBaseline(typeof data?.baseline_conversion_rate === 'number' ? data.baseline_conversion_rate : null);
    }, setInfluenceLoading);
    load(getCdpPeakHours, setPeak, setPeakLoading);
  }, [clientId, rangeStart, rangeEnd, includeBots]);

  // Compute site-average conversion rate for landing pages
  const totalVisitors = landing.reduce((s, p) => s + p.visitors, 0);
  const totalBookClicks = landing.reduce((s, p) => s + p.book_clicks, 0);
  const siteAvgRate = totalVisitors > 0 ? (totalBookClicks / totalVisitors) * 100 : 0;

  // Peak hours heatmap data
  const peakMax = peak.length > 0
    ? Math.max(...peak.map(p => peakMode === "high_intent" ? p.high_intent_visitors : p.visitors))
    : 1;

  // Find peak window
  const peakCell = peak.length > 0
    ? peak.reduce((best, p) => {
        const v = peakMode === "high_intent" ? p.high_intent_visitors : p.visitors;
        return v > (peakMode === "high_intent" ? best.high_intent_visitors : best.visitors) ? p : best;
      }, peak[0])
    : null;

  return (
    <div className="space-y-6">

      {/* ═══ 5A: LANDING PAGE PERFORMANCE ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <PanelTakeaway panelKey="landing_pages" say={say} />
          <h3 className="gi-panel-title text-gray-900 dark:text-white">Landing Page Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Page</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-24">Visitors</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-24">Book Clicks</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-24">Bookings</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-20">Video</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-20">Phone</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-24">Conv Rate</th>
              </tr>
            </thead>
            <tbody>
              {landingLoading ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : landing.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-400 text-sm">No landing page data</td></tr>
              ) : landing.map((r, i) => {
                const path = stripDomain(r.landing_page);
                // P1-1: landing pages with <10 visitors → noisy conversion rates. Badge + dim.
                const lowVolume = (r.visitors || 0) < 10;
                const cappedRate = Math.min(r.conversion_rate || 0, 100);
                return (
                  <tr key={r.landing_page} className={`border-b border-gray-100 dark:border-gray-800 ${i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""} ${lowVolume ? "opacity-60" : ""}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-700 dark:text-gray-300 truncate max-w-[300px]" title={path}>{truncate(path, 45)}</span>
                        <a href={r.landing_page} target="_blank" rel="noopener noreferrer" className="text-gray-300 dark:text-gray-600 hover:text-blue-500 shrink-0">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                        {lowVolume && (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300" title="Under 10 visitors — rate is not yet directional.">Low</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.visitors}</td>
                    <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.book_clicks}</td>
                    <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.bookings}</td>
                    <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.video_plays}</td>
                    <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.phone_clicks}</td>
                    <td className={`px-4 py-2.5 text-right text-xs font-medium ${
                      lowVolume ? "text-gray-400 dark:text-gray-500"
                        : cappedRate > siteAvgRate ? "text-emerald-600 dark:text-emerald-400"
                        : cappedRate < siteAvgRate && cappedRate > 0 ? "text-red-500 dark:text-red-400"
                        : "text-gray-500 dark:text-gray-400"
                    }`}>
                      {cappedRate.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ 5B: CONTENT INFLUENCE SCORE ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <h3 className="gi-panel-title text-gray-900 dark:text-white">Content Influence Score</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Which pages bookers saw anywhere in their journey — not just as a landing page — ranked by conversion influence. A score of 100 equals the site baseline conversion rate{influenceBaseline != null ? ` (${influenceBaseline}%)` : ''}; above 100 means this page's visitors convert better than average.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Page</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Bookers Saw This</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Influence Score</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">All Visitors</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Booker Rate</th>
              </tr>
            </thead>
            <tbody>
              {influenceLoading ? (
                <tr><td colSpan={5} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : !Array.isArray(influence) || influence.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-sm">No influence data (need bookings in this period)</td></tr>
              ) : influence.map((r, i) => {
                // Field names match fn_get_cdp_content_influence's actual output
                // (landing_page / converted / influence_score / visitors / conversion_rate).
                // influence_score is an INDEX where 100 = site baseline (can exceed 100),
                // so it is shown as a number, not a percentage. The bar uses a 0–200 scale
                // (100 score → 50% bar) so above-baseline pages still show gradation.
                const score = Number(r.influence_score) || 0;
                const barW = Math.min(Math.max(score, 0), 200) / 2;
                return (
                <tr key={`${r.landing_page || "page"}-${i}`} className={`border-b border-gray-100 dark:border-gray-800 ${i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""}`}>
                  <td className="px-4 py-2.5 text-xs text-gray-700 dark:text-gray-300 truncate max-w-[300px]" title={r.landing_page || ""}>{truncate(stripDomain(r.landing_page), 45)}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">
                    {r.converted ?? 0} bookers
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-20 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-indigo-500" style={{ width: `${barW}%` }}></div>
                      </div>
                      <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400">{score}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-600 dark:text-gray-400">{r.visitors ?? 0}</td>
                  <td className="px-4 py-2.5 text-right text-xs text-gray-600 dark:text-gray-400">{r.conversion_rate ?? 0}%</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ 5C: PEAK BROWSING WINDOWS (7x24 Heatmap) ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <PanelTakeaway panelKey="peak_windows" say={say} />
          <h3 className="gi-panel-title text-gray-900 dark:text-white">Peak Browsing Windows</h3>
            {peakCell && !peakLoading && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                Highest traffic: {DAY_LABELS[peakCell.day_of_week]} {HOUR_LABELS[peakCell.hour]}
              </p>
            )}
          </div>
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setPeakMode("all")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${peakMode === "all" ? "bg-[#32B1FF] text-white" : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400"}`}
            >
              All Visitors
            </button>
            <button
              type="button"
              onClick={() => setPeakMode("high_intent")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${peakMode === "high_intent" ? "bg-[#32B1FF] text-white" : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400"}`}
            >
              High Intent
            </button>
          </div>
        </div>

        {peakLoading ? (
          <div className="h-56 flex items-center justify-center text-gray-400">Loading...</div>
        ) : peak.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-gray-400 text-sm">No browsing data</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="inline-grid gap-0.5" style={{ gridTemplateColumns: `48px repeat(24, 1fr)` }}>
              {/* Hour headers */}
              <div></div>
              {HOUR_LABELS.map(h => (
                <div key={h} className="text-[9px] text-gray-400 dark:text-gray-500 text-center pb-1">{h}</div>
              ))}

              {/* Grid rows — one per day */}
              {DAY_LABELS.map((dayLabel, dow) => (
                <React.Fragment key={dow}>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 font-medium flex items-center pr-2 justify-end">{dayLabel}</div>
                  {Array.from({ length: 24 }, (_, hour) => {
                    const cell = peak.find(p => p.day_of_week === dow && p.hour === hour);
                    const value = cell ? (peakMode === "high_intent" ? cell.high_intent_visitors : cell.visitors) : 0;
                    const intensity = peakMax > 0 ? value / peakMax : 0;

                    return (
                      <div
                        key={`${dow}-${hour}`}
                        className="w-full aspect-square rounded-sm relative group cursor-default"
                        style={{
                          backgroundColor: intensity === 0
                            ? "transparent"
                            : `rgba(99, 102, 241, ${0.08 + intensity * 0.82})`,
                          minWidth: 18,
                          minHeight: 18,
                        }}
                        title={`${dayLabel} ${HOUR_LABELS[hour]}: ${value} ${peakMode === "high_intent" ? "high-intent" : ""} visitors`}
                      >
                        {value > 0 && intensity > 0.5 && (
                          <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-white">{value}</span>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-2 mt-3 justify-end">
              <span className="text-[10px] text-gray-400 dark:text-gray-500">Less</span>
              {[0.1, 0.3, 0.5, 0.7, 0.9].map(v => (
                <div key={v} className="w-3 h-3 rounded-sm" style={{ backgroundColor: `rgba(99, 102, 241, ${0.08 + v * 0.82})` }}></div>
              ))}
              <span className="text-[10px] text-gray-400 dark:text-gray-500">More</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
